/**
 * Messaging brief for an existing campaign (meeting 2026-09-18).
 *
 * Opens the same interview + prompt editor as the create wizard, loaded
 * with the campaign's saved brief. "Confirm and save" stores a new version
 * (saving is confirming, and a campaign that needs a brief can send once
 * one is confirmed). The History tab lists the last saved versions and
 * shows what changed between any version and the text on screen now.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  emptyCampaignBrief,
  missingBriefFields,
  normalizeCampaignBrief,
  renderCampaignBrief,
  type CampaignBrief,
  type OfferConfig,
} from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useApi } from '@/hooks/use-api'
import { fmtLocale } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useCampaignBrief, useSaveCampaignBrief } from '../use-campaigns'
import { CampaignBriefEditor } from './brief-editor'
import { diffLines } from './line-diff'

export function CampaignBriefDialog({
  campaignId,
  campaignName,
  open,
  onOpenChange,
}: {
  readonly campaignId: string
  readonly campaignName: string
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}) {
  const apiFetch = useApi()
  const { data, isLoading, error } = useCampaignBrief(campaignId, open)
  const save = useSaveCampaignBrief()
  const [draft, setDraft] = useState<CampaignBrief>(emptyCampaignBrief)
  const [view, setView] = useState<'edit' | 'history'>('edit')
  const [compareTo, setCompareTo] = useState<number | null>(null)

  const { data: offer } = useQuery({
    queryKey: ['settings', 'offer'],
    queryFn: async () => {
      const res = await apiFetch<OfferConfig>('/api/settings/offer')
      return res.success ? res.data : null
    },
    staleTime: 5 * 60_000,
    enabled: open,
  })

  // Load the saved brief each time the dialog opens.
  useEffect(() => {
    if (!open || !data) return
    setDraft(normalizeCampaignBrief(data.brief) ?? emptyCampaignBrief())
    setView('edit')
    setCompareTo(data.versions[1]?.version ?? null)
  }, [open, data])

  const current = useMemo(() => renderCampaignBrief(draft), [draft])
  const missing = missingBriefFields(draft)
  const dirty = data ? current !== data.rendered : false
  const older = data?.versions.find((v) => v.version === compareTo)
  const diff = useMemo(() => (older ? diffLines(older.rendered, current) : []), [older, current])

  async function handleSave() {
    await save.mutateAsync({ id: campaignId, brief: draft })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[88vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Messaging brief: {campaignName}</DialogTitle>
          <DialogDescription>
            What the AI knows about your offer and how it should write for this campaign. Nothing here is hidden.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            {error ? (error instanceof Error ? error.message : 'Failed to load') : <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</>}
          </div>
        ) : (
          <>
            <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs">
              {data.confirmed_at ? (
                <span className="rounded bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-400">
                  Confirmed v{data.version} · {fmtLocale(data.confirmed_at)}
                </span>
              ) : (
                <span className="rounded bg-amber-500/15 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-400">
                  {data.required ? 'Not confirmed yet: this campaign cannot send until you confirm' : 'No brief yet'}
                </span>
              )}
              {data.versions.length > 0 && (
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant={view === 'edit' ? 'secondary' : 'ghost'} className="h-7 text-xs" onClick={() => setView('edit')}>
                    Edit
                  </Button>
                  <Button size="sm" variant={view === 'history' ? 'secondary' : 'ghost'} className="h-7 text-xs" onClick={() => setView('history')}>
                    History ({data.versions.length})
                  </Button>
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {view === 'edit' ? (
                <CampaignBriefEditor
                  value={draft}
                  onChange={setDraft}
                  offer={offer}
                  confirmSlot={
                    <div className="space-y-2 border-t pt-3">
                      <p className="text-xs text-muted-foreground">
                        Saving confirms this prompt. New messages for this campaign use it right away.
                      </p>
                      <Button
                        className="w-full"
                        disabled={missing.length > 0 || save.isPending || (!dirty && Boolean(data.confirmed_at))}
                        onClick={() => void handleSave()}
                      >
                        {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {!dirty && data.confirmed_at ? 'Confirmed, no changes' : 'Confirm and save'}
                      </Button>
                      {save.isError && (
                        <p className="text-sm text-destructive">{save.error instanceof Error ? save.error.message : 'Save failed'}</p>
                      )}
                    </div>
                  }
                />
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Compare the text on screen with</span>
                    <select
                      value={compareTo ?? ''}
                      onChange={(e) => setCompareTo(Number(e.target.value))}
                      className="h-8 rounded-md border bg-transparent px-2 text-sm"
                    >
                      {data.versions.map((v) => (
                        <option key={v.version} value={v.version}>
                          v{v.version} · {fmtLocale(v.created_at)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {older ? (
                    <pre className="max-h-[480px] overflow-auto whitespace-pre-wrap rounded-md border p-3 font-mono text-xs leading-relaxed">
                      {diff.every((d) => d.kind === 'same') ? (
                        <span className="text-muted-foreground">No differences.</span>
                      ) : (
                        diff.map((d, i) => (
                          <div
                            key={i}
                            className={cn(
                              d.kind === 'add' && 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
                              d.kind === 'del' && 'bg-red-500/15 text-red-800 line-through dark:text-red-300',
                            )}
                          >
                            {d.kind === 'add' ? '+ ' : d.kind === 'del' ? '- ' : '  '}
                            {d.text || ' '}
                          </div>
                        ))
                      )}
                    </pre>
                  ) : (
                    <p className="text-sm text-muted-foreground">Pick a version.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
